import { createFileRoute } from '@tanstack/react-router'
import { requireGuest } from '@/lib/auth-guard'

const API_ORIGIN = (() => {
  const base = import.meta.env.VITE_API_BASE_URL
  if (!base) return undefined
  try {
    return new URL(base).origin
  } catch {
    return undefined
  }
})()

function safeRedirect(raw: unknown): string | undefined {
  // Untrusted external search param: only allow app-relative paths,
  // never protocol-relative ("//") or backslash-tricked URLs.
  if (typeof raw !== 'string') return undefined
  if (raw.startsWith('/') && !raw.startsWith('//') && !raw.startsWith('/\\')) return raw
  // Single absolute-URL exception: the API's OAuth consent page (MCP
  // connector flow) bounces through login and must return to
  // /oauth2/authorize on the API origin.
  try {
    const url = new URL(raw)
    if (API_ORIGIN && url.origin === API_ORIGIN && url.pathname === '/oauth2/authorize') {
      return raw
    }
  } catch {
    // not an absolute URL — fall through
  }
  return undefined
}

/** True when the (already-validated) redirect is the absolute OAuth consent
 * URL rather than an app-relative path. */
export function isExternalOAuthRedirect(redirect: string | undefined): boolean {
  return typeof redirect === 'string' && !redirect.startsWith('/')
}

export const Route = createFileRoute('/login')({
  validateSearch: (raw: Record<string, unknown>): { redirect?: string; email?: string } => ({
    redirect: safeRedirect(raw.redirect),
    email: typeof raw.email === 'string' ? raw.email : undefined,
  }),
  // An already-signed-in user arriving with the OAuth consent redirect must
  // NOT be bounced into the app (that strands the connector flow) — the
  // LoginPage handles that case by refreshing the session cookie and
  // forwarding to the consent page.
  beforeLoad: ({ search }) => {
    if (isExternalOAuthRedirect(search.redirect)) return
    return requireGuest()
  },
})
